import { useEffect, useState } from 'react';

import { useSearchParams } from 'next/navigation';
import { fetchWatchAndWinLeaderboard } from 'src/services/apiRequests/WatchAndWinService';
import {
  parseGameMapperId,
  triggerContestWineersDateSelect
} from 'src/utils/watchAndWinUtils';

import GameDateFilter from './GameDateFilter';
import Table from './GameTable';
import { winningPrizeImageStyle, placeHolderTextStyle } from './WinnersStyles';

interface WinnersList {
  date: string;
  rewardsInfo: { imageUrl: string; placeHolderText: string };
  winners: { name: string; location: string }[];
}
const Winners = () => {
  const [selectedDate, setSelectedDate] = useState('');
  const [winnersData, setWinnersData] = useState([]);
  const [dateFilters, setDateFilters] = useState<string[]>([]);
  const [activeDates, setActiveDates] = useState([]);
  const [winnersList, setWinnerList] = useState<WinnersList | null>(null);

  const searchParams = useSearchParams();
  const gameMapperId = parseGameMapperId(
    searchParams.get('game_mapper_id') || ''
  );
  useEffect(() => {
    (async () => {
      const data = await fetchWatchAndWinLeaderboard(
        '',
        '',
        gameMapperId as string
      );
      const dates = data?.entries?.map((d: any, i: number) => d?.title?.value);
      setActiveDates(data?.activeDates);
      const winners = formatData(data);
      setWinnersData(winners);
      setDateFilters(dates);
      setSelectedDate(dates[0]);
      setWinnerList(winners.find((winner: any) => winner.date === dates[0]));
    })();
  }, []);

  const formatData = (data: any) => {
    return data?.entries
      ?.flatMap((entry: any) =>
        entry.results.map((result: any) => ({
          date: entry.title.value,
          rewardsInfo: result?.rewardsInfo,
          winners: result?.winners
        }))
      )
      .reduce((acc: any, curr: any) => {
        const existingDate = acc.find((item: any) => item.date === curr.date);
        if (existingDate) {
          existingDate.winners = [...existingDate.winners, ...curr.winners];
        } else {
          acc.push({
            date: curr.date,
            rewardsInfo: curr.rewardsInfo,
            winners: curr.winners
          });
        }
        return acc;
      }, []);
  };
  const getWinnersDataByDate = async (date: string) => {
    const data = await fetchWatchAndWinLeaderboard(
      date,
      'date',
      gameMapperId as string
    );

    const winners = formatData(data);
    setWinnerList(winners?.find((winner: any) => winner?.date === date));
  };

  // function to handle date filters chip select
  const handleDateSelect = (date: string) => {
    const dates = [...dateFilters];
    setSelectedDate(date);
    if (dates && dates.length > 0 && dates[0] === date) {
      getWinnersDataByDate(date);
    } else if (dates.includes(date)) {
      setWinnerList(
        winnersData.find((winner: any) => winner?.date === date) || null
      );
    }
    triggerContestWineersDateSelect(gameMapperId as string, date);
  };

  // function to handle dropdown date change
  const handleDateChange = (date: string) => {
    setSelectedDate(date);
    const dates = [...dateFilters];
    if (dates.length > 3) {
      dates.shift();
    }
    if (!dates.includes(date)) {
      setDateFilters([date, ...dates]);
      getWinnersDataByDate(date);
    } else {
      setWinnerList(
        winnersData.find((winner: any) => winner?.date === date) || null
      );
    }
    triggerContestWineersDateSelect(gameMapperId as string, date);
  };

  const handleCloseDate = () => {
    const dates = [...dateFilters];
    dates.shift();
    setDateFilters(dates);
    setSelectedDate(dates[0]);
    setWinnerList(
      winnersData.find((winner: any) => winner?.date === dates[0]) || null
    );
  };
  return (
    <>
      <GameDateFilter
        dateFilters={dateFilters}
        activeDates={activeDates}
        selectedDate={selectedDate}
        setSelectedDate={handleDateSelect}
        handleDateChange={handleDateChange}
        closeDate={handleCloseDate}
      />
      <div>
        <img
          css={winningPrizeImageStyle}
          src={`${winnersList?.rewardsInfo?.imageUrl}`}
          alt={`${winnersList?.rewardsInfo?.placeHolderText || ''}`}
        />
        {!winnersList?.winners && (
          <div css={placeHolderTextStyle}>
            {winnersList?.rewardsInfo?.placeHolderText}
          </div>
        )}
      </div>

      {winnersList?.winners && <Table data={winnersList} />}
    </>
  );
};

export default Winners;
